import type { SqlRunner } from '../../../lib/db.js';
import {
  DROP_BOX_CLASSIFICATION_JOB_TYPE,
  DROP_BOX_FILE_TYPES,
  type DropBoxFileType,
} from '../../constants.js';
import type { IDropBoxClassifyResultItem } from '../../contracts/schemas/drop-box.js';
import {
  getLatestAiProcessingJobDb,
  getQueueProcessorStatusDb,
  type QueueProcessorStatusRow,
} from '../db/drop-box/classification-jobs.js';

type ClassificationStatus = IDropBoxClassifyResultItem['status'];

/** What the classification job stores: its result, or the error it hit. */
interface ClassificationOutcome {
  result?: { type?: string; confidence?: number; reasoning?: string };
  error?: string;
}

const KNOWN_TYPES = new Set<string>(Object.values(DROP_BOX_FILE_TYPES));

/**
 * A stored result wins, then a stored error; otherwise the queue processor
 * says how far it got, and no processor yet is `pending`.
 */
const classificationStatus = (
  processor: QueueProcessorStatusRow | null,
  hasResult: boolean,
  hasError: boolean,
): ClassificationStatus => {
  if (hasError && !hasResult) return 'failed';
  if (hasResult) return 'completed';
  switch (processor?.status) {
    case 'completed':
      return 'completed';
    case 'failed':
      return 'failed';
    case 'processing':
      return 'processing';
    default:
      return 'pending';
  }
};

/** The drop-box: files uploaded unsorted, classified by AI before filing. */
export class DropBoxService {
  constructor(private readonly deps: { db: SqlRunner }) {}

  /**
   * Each file's latest fast classification, in the order asked. A file
   * without one — or whose latest one another company started — is
   * `pending`. A classified type outside `DROP_BOX_FILE_TYPES` is answered
   * as no classification.
   */
  async getClassificationResults(
    companyId: string,
    fileIds: string[],
  ): Promise<IDropBoxClassifyResultItem[]> {
    const { db } = this.deps;
    const results: IDropBoxClassifyResultItem[] = [];

    for (const fileId of fileIds) {
      const job = await getLatestAiProcessingJobDb(db, {
        fileId,
        jobType: DROP_BOX_CLASSIFICATION_JOB_TYPE,
      });
      if (!job || job.createdByCompanyId !== companyId) {
        results.push({
          fileId,
          aiProcessingJobId: null,
          status: 'pending',
          classification: null,
          error: null,
        });
        continue;
      }

      const processor = job.queueJobProcessorId
        ? await getQueueProcessorStatusDb(db, job.queueJobProcessorId)
        : null;
      const outcome = job.result as ClassificationOutcome | null;
      const classification = outcome?.result ?? null;
      const type =
        typeof classification?.type === 'string' &&
        KNOWN_TYPES.has(classification.type)
          ? (classification.type as DropBoxFileType)
          : null;

      results.push({
        fileId,
        aiProcessingJobId: job.id,
        status: classificationStatus(
          processor,
          Boolean(classification),
          Boolean(outcome?.error),
        ),
        classification: type
          ? {
              type,
              confidence: classification?.confidence ?? 0,
              reasoning: classification?.reasoning ?? '',
            }
          : null,
        error: outcome?.error ?? processor?.error ?? null,
      });
    }

    return results;
  }
}
